import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useOutletContext } from 'react-router-dom';
import Hero from '../components/Hero';
import GourmetSelectionSection from '../components/GourmetSelectionSection';
import NewsletterSection from '../components/NewsletterSection';
import PageFooter from '../components/PageFooter';
import { useLanguage } from '../i18n/LanguageContext';
import { isBrowserHebrew, setLangPref, getLangPref } from '../App';
import { getTopFavorites } from '../services/recipeService';
import { getRecipeImageUrl, getTotalTimeMinutes, formatMinutes } from '../utils/recipeUtils';

const heroImage = {
  src: 'https://lh3.googleusercontent.com/aida-public/AB6AXuDOm2EgnzRhnIvQZfKhxeNaNpyFOwTRs_s5ZfMSRgzonZqoBBZiryhNylYcX0zFAkTFMFHmvCTRSMQvoGiozEMiteuADIWaElNjEHe4SyXECFHzx5Er2XuZ212NUCOcjSuFi9DNi1fiem2uwS9is9vXFMuSq_HbYHOatdSCN3J0g4516u-BIODQlhy0V6_8JyScj6l_U4A1clE-j8YU1L_twPa4OPwILFspVri3gbXNr1g0LsJGhmTwmwX-XA19Gm6p8lb5RfAFZ4k',
};

const recipeToCard = (recipe, lang, nutritionTagLabels) => {
  const totalTime = getTotalTimeMinutes(recipe);
  const tags = recipe.nutrition?.nutritionTags ?? [];
  const tag = tags[0] ? (nutritionTagLabels[tags[0]] ?? tags[0]) : null;
  const secondaryTag = tags[1] ? (nutritionTagLabels[tags[1]] ?? tags[1]) : null;
  return {
    id: recipe._id || recipe.id,
    recipe,
    title: recipe.title,
    tag,
    secondaryTag,
    time: totalTime ? formatMinutes(totalTime, lang) : '—',
    calories: recipe.nutrition?.calories ? `${recipe.nutrition.calories} kcal` : '—',
    image: { src: getRecipeImageUrl(recipe), alt: recipe.title },
  };
};

const HomePage = () => {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const { currentUser, onAuthRequiredMessage } = useOutletContext() ?? {};
  const [gourmetCards, setGourmetCards] = useState([]);

  const nutritionTagLabels = t.nutrition?.tags ?? {};

  useEffect(() => {
    const controller = new AbortController();
    getTopFavorites(lang, controller.signal)
      .then((recipes) => setGourmetCards(recipes.map((r) => recipeToCard(r, lang, nutritionTagLabels))))
      .catch(() => {});
    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // Auto-redirect on first visit: if no explicit language preference has been stored
  // and the browser is set to Hebrew, redirect to the Hebrew route.
  if (lang === 'en' && getLangPref() === null && isBrowserHebrew()) {
    setLangPref('he');
    return <Navigate to="/heb" replace />;
  }

  const handleGourmetSelect = (card) => {
    const path = lang === 'he' ? `/heb/recipes/${card.id}` : `/recipes/${card.id}`;
    navigate(path, { state: { recipe: card.recipe } });
  };

  const heroContent = {
    headingLead: t.hero.headingLead,
    headingTrail: t.hero.headingTrail,
    highlight: t.hero.highlight,
    description: t.hero.description,
    primaryAction: t.hero.primaryAction,
    secondaryAction: {
      label: t.hero.generateAction,
      href: lang === 'he' ? '/heb/explorer?mode=generate' : '/explorer?mode=generate',
      disabled: !currentUser,
      onClick: (event) => {
        if (currentUser) {
          return;
        }

        event.preventDefault();
        onAuthRequiredMessage?.(t.explorer.generateLoginRequired);
      },
    },
    image: { src: heroImage.src, alt: t.hero.imageAlt },
    weeklyPick: t.hero.weeklyPick,
    weeklyPickLabel: t.hero.weeklyPickLabel,
  };

  return (
    <div className="app-shell">
      <main>
        <Hero {...heroContent} />
        <GourmetSelectionSection cards={gourmetCards} onSelect={handleGourmetSelect} />
        <NewsletterSection />
      </main>
      <PageFooter />
    </div>
  );
};

export default HomePage;
